import { useState, useEffect, useRef } from 'preact/hooks';
import { useAuth } from '../auth/context';
import { getStats } from '../api/stats';
import { getHealth } from '../api/health';
import { getErrors } from '../api/logs';
import type { Route, TopSection } from '../hooks/use-route';

// Re-export TabId for backward compatibility
export type TabId =
  | 'overview'
  | 'products'
  | 'ingredients'
  | 'users'
  | 'verdicts'
  | 'tips'
  | 'cache'
  | 'logs'
  | 'health'
  | 'system'
  | 'schema'
  | 'version';

interface SidebarProps {
  route: Route;
  onNavigate: (hash: string) => void;
  open: boolean;
  onClose: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

interface NavItem {
  id: string;         // hash route to navigate to
  page: string;       // page key for active matching
  section: TopSection;
  label: string;
  icon: string;
  badge?: 'count' | 'health' | 'errors';
}

const SECTIONS: { label: string; items: NavItem[] }[] = [
  {
    label: 'Command Center',
    items: [
      {
        id: 'overview',
        page: 'overview',
        section: 'overview',
        label: 'Overview',
        icon: '<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>',
      },
    ],
  },
  {
    label: 'Catalog',
    items: [
      {
        id: 'catalog/products',
        page: 'products',
        section: 'catalog',
        label: 'Products',
        icon: '<path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/>',
        badge: 'count',
      },
      {
        id: 'catalog/ingredients',
        page: 'ingredients',
        section: 'catalog',
        label: 'Ingredients',
        icon: '<path d="M10 2v7.53a2 2 0 0 1-.2.9L4.7 20.5a1 1 0 0 0 .9 1.5h12.8a1 1 0 0 0 .9-1.5l-5.1-10.07a2 2 0 0 1-.2-.9V2"/><path d="M8.5 2h7"/><path d="M7 16.5h10"/>',
        badge: 'count',
      },
      {
        id: 'catalog/users',
        page: 'users',
        section: 'catalog',
        label: 'Users',
        icon: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
        badge: 'count',
      },
      {
        id: 'catalog/verdicts',
        page: 'verdicts',
        section: 'catalog',
        label: 'Verdicts',
        icon: '<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/>',
      },
      {
        id: 'catalog/tips',
        page: 'tips',
        section: 'catalog',
        label: 'Tips',
        icon: '<path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>',
      },
    ],
  },
  {
    label: 'Operations',
    items: [
      {
        id: 'ops/health',
        page: 'health',
        section: 'ops',
        label: 'Health',
        icon: '<path d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2"/>',
        badge: 'health',
      },
      {
        id: 'ops/logs',
        page: 'logs',
        section: 'ops',
        label: 'Logs',
        icon: '<path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>',
        badge: 'errors',
      },
      {
        id: 'ops/cache',
        page: 'cache',
        section: 'ops',
        label: 'Cache',
        icon: '<ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v14a9 3 0 0 0 18 0V5"/><path d="M3 12a9 3 0 0 0 18 0"/>',
      },
      {
        id: 'ops/schema',
        page: 'schema',
        section: 'ops',
        label: 'Schema',
        icon: '<ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v4a9 3 0 0 0 18 0V5"/><path d="M3 9v4a9 3 0 0 0 18 0V9"/><path d="M3 13v4a9 3 0 0 0 18 0v-4"/>',
      },
      {
        id: 'ops/version',
        page: 'version',
        section: 'ops',
        label: 'Version',
        icon: '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>',
      },
    ],
  },
];

function SvgIcon({ path }: { path: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      dangerouslySetInnerHTML={{ __html: path }}
    />
  );
}

function formatCount(n: number): string {
  if (n >= 10000) return `${(n / 1000).toFixed(0)}k`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

export function Sidebar({ route, onNavigate, open, onClose, collapsed, onToggleCollapse }: SidebarProps) {
  const { logout } = useAuth();
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [healthStatus, setHealthStatus] = useState<'ok' | 'degraded' | 'unknown'>('unknown');
  const [errorCount, setErrorCount] = useState(0);
  const mounted = useRef(false);

  const env = import.meta.env.VITE_API_BASE_URL?.includes('localhost')
    ? 'staging'
    : import.meta.env.VITE_API_BASE_URL?.includes('staging')
      ? 'staging'
      : 'production';

  useEffect(() => {
    if (mounted.current) return;
    mounted.current = true;

    getStats()
      .then((s) => {
        setCounts({
          products: s.totalProducts,
          users: s.totalUsers,
          ingredients: s.totalIngredients,
        });
      })
      .catch(() => {});

    getHealth()
      .then((h) => {
        const degraded = Object.values(h.dependencies).filter((v) => v !== 'ok').length;
        setHealthStatus(degraded > 0 ? 'degraded' : 'ok');
      })
      .catch(() => setHealthStatus('unknown'));

    getErrors()
      .then((e) => setErrorCount(e.entries.length))
      .catch(() => {});
  }, []);

  const handleNav = (hash: string) => {
    onNavigate(hash);
    onClose();
  };

  const isActive = (item: NavItem): boolean => {
    if (item.section === 'overview' && route.section === 'overview') return true;
    return route.section === item.section && route.page === item.page;
  };

  return (
    <>
      <div
        class={`sidebar-backdrop ${open ? 'open' : ''}`}
        onClick={onClose}
      />
      <aside class={`sidebar ${open ? 'open' : ''} ${collapsed ? 'collapsed' : ''}`}>
        <div class="sidebar-brand">
          <div class="dot" />
          <span>Sift</span>
          <button class="sidebar-collapse-btn" onClick={onToggleCollapse} title={collapsed ? 'Expand' : 'Collapse'}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        </div>
        <nav class="sidebar-nav">
          {SECTIONS.map((section) => (
            <div key={section.label}>
              <div class="sidebar-section-label">{section.label}</div>
              {section.items.map((item) => (
                <button
                  key={item.id}
                  class={`sidebar-item ${isActive(item) ? 'active' : ''}`}
                  onClick={() => handleNav(item.id)}
                  title={collapsed ? item.label : undefined}
                >
                  <SvgIcon path={item.icon} />
                  <span class="sidebar-item-label">{item.label}</span>
                  {item.badge === 'count' && counts[item.page] != null && (
                    <span class="sidebar-item-badge">
                      {formatCount(counts[item.page])}
                    </span>
                  )}
                  {item.badge === 'health' && (
                    <span class={`sidebar-status-dot ${healthStatus}`} />
                  )}
                  {item.badge === 'errors' && errorCount > 0 && (
                    <span class="sidebar-error-count">{errorCount}</span>
                  )}
                </button>
              ))}
            </div>
          ))}
        </nav>
        <div class="sidebar-footer">
          <div
            class="env-badge"
            style={{
              background: env === 'production' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(245, 158, 11, 0.15)',
              color: env === 'production' ? '#f87171' : '#fbbf24',
            }}
          >
            {env.toUpperCase()}
          </div>
          <button class="sidebar-signout" onClick={logout}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            <span>Sign out</span>
          </button>
        </div>
      </aside>
    </>
  );
}
